import {Card, Table, Tag, Button, Tooltip, Typography} from 'antd';
import UserLink from 'components/userLink/UserLink';
import {DOCUMENT_STATUS_COLORS} from '../kybConstants';
import {formatAuthorityBasis, formatEnumLabel, personName} from '../kybUtils';
import styles from '../kybDashboard.module.scss';

const {Text} = Typography;

/**
 * Who owns the business, and who is speaking for it.
 *
 * `contactAuthorityBasis` and `hasNoMajorityOwner` sit here rather than in the
 * header because they are what explain the shape of the checklist: a contact
 * holding power of attorney adds a requirement row, and a business with no
 * majority owner is why no single owner is listed at 25%+.
 *
 * The owner who is the account administrator is the only one with a user
 * record, so their name opens that user's KYC page; the others are plain text.
 */
export const KybBeneficialOwners = ({
  account,
  onPreview,
  canPreview,
  previewingId,
}) => {
  const owners = account?.beneficialOwners || [];
  const administratorId = account?.primaryContact?.id;

  const columns = [
    {
      title: 'Owner',
      key: 'fullName',
      render: (_, record) => {
        const name = record.fullName || personName(record);
        return (
          <div className={styles.entityCell}>
            {record.isPrimaryContact && administratorId ? (
              <UserLink userId={administratorId} tab="kyc-status">
                {name}
              </UserLink>
            ) : (
              <span>{name}</span>
            )}
            {record.isPrimaryContact && (
              <Tag color="purple">Account administrator</Tag>
            )}
          </div>
        );
      },
    },
    {
      title: 'Ownership',
      key: 'ownershipPct',
      render: (_, record) =>
        record.ownershipPct == null ? '—' : `${record.ownershipPct}%`,
    },
    {
      title: 'Nationality',
      dataIndex: 'nationality',
      key: 'nationality',
      render: value => value || '—',
    },
    {
      title: 'ID document',
      key: 'idDocument',
      render: (_, record) => {
        const doc = record.idDocument;
        if (!doc) return <Text type="secondary">None on file</Text>;
        const openable = canPreview(doc);
        return (
          <div className={styles.rowActions}>
            <Tag color={DOCUMENT_STATUS_COLORS[doc.status] || 'default'}>
              {formatEnumLabel(doc.status)}
            </Tag>
            <Tooltip
              title={openable ? '' : 'This file has no usable storage link.'}>
              <Button
                size="small"
                disabled={!openable}
                loading={previewingId === doc.id}
                onClick={() => onPreview(doc)}>
                View
              </Button>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  return (
    <Card
      title="Beneficial owners"
      className={styles.card}
      extra={<Text type="secondary">{owners.length} declared</Text>}>
      {account?.contactAuthorityBasis && (
        <Text type="secondary" className={styles.cardNote}>
          Contact acts as: {formatAuthorityBasis(account.contactAuthorityBasis)}
        </Text>
      )}
      {account?.hasNoMajorityOwner && (
        <Text type="secondary" className={styles.cardNote}>
          The business declared that no single person holds a majority stake.
        </Text>
      )}
      <Table
        rowKey={record => record.id}
        size="small"
        columns={columns}
        dataSource={owners}
        pagination={false}
        locale={{emptyText: 'No beneficial owners declared.'}}
      />
    </Card>
  );
};
